import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import * as RI from '../RiderInformation';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

const riderCodes: RI.NewYorkRiderCode[] = [
  {
    code: 'WBR3',
    displayName: 'WBR3',
    units: [1, 2, 3, 4],
  },
];

type CancerProps = {
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  policyOption: StringUnion;
  cwa: StringUnion;
  section125: boolean;
  riderInformation: RI.default;
};

export class InvalidNewYorkCancer {
  public readonly key = 'InvalidNewYorkCancer';

  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly policyOption: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly riderInformation: RI.default
  ) {}
}

export class ValidNewYorkCancer {
  public static createApp({
    planType,
    premium,
    calculatedPremium,
    policyOption,
    cwa,
    section125,
    riderInformation,
  }: CancerProps) {
    return this.create(planType, premium, calculatedPremium, policyOption, cwa, section125, riderInformation);
  }

  public static create(
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    policyOption: StringUnion,
    cwa: StringUnion,
    section125: boolean,
    riderInformation: RI.default
  ) {
    return planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      section125 &&
      riderInformation.codes.size > 0 &&
      riderInformation.amounts.size > 0
      ? new ValidNewYorkCancer(planType, premium, calculatedPremium, policyOption, cwa, section125, riderInformation)
      : new InvalidNewYorkCancer(planType, premium, calculatedPremium, policyOption, cwa, section125, riderInformation);
  }

  public readonly key = 'ValidNewYorkCancer';
  constructor(
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly policyOption: StringUnion,
    public readonly cwa: StringUnion,
    public readonly section125: boolean,
    public readonly riderInformation: RI.default
  ) {}
}

export type NewYorkCancer = InvalidNewYorkCancer | ValidNewYorkCancer;

export type NewYorkCancerOptions<TReturn> = ToPartialMatchOptions<NewYorkCancer, 'key', TReturn>;

export const matchGroupCancer = <TReturn>(options: NewYorkCancerOptions<TReturn>) => (app: NewYorkCancer): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkCancer':
      return options.InvalidNewYorkCancer ? options.InvalidNewYorkCancer(app) : options.default(app);
    case 'ValidNewYorkCancer':
      return options.ValidNewYorkCancer ? options.ValidNewYorkCancer(app) : options.default(app);
  }
};

export const EmptyNewYorkCancer = new InvalidNewYorkCancer(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  new RI.default(I.List<string>(), I.List<number>(), riderCodes)
);

export const changePlanType = (planType: string) => (app: NewYorkCancer) =>
  ValidNewYorkCancer.createApp({...app, planType: NonEmptyString.create(planType)});

export const changePolicyOption = (policyOption: string) => (app: NewYorkCancer) =>
  ValidNewYorkCancer.createApp({...app, policyOption: NonEmptyString.create(policyOption)});

export const changePremium = (premium: string) => (app: NewYorkCancer) =>
  ValidNewYorkCancer.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkCancer) =>
  ValidNewYorkCancer.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: NewYorkCancer) =>
  ValidNewYorkCancer.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeSection125 = (section125: boolean) => (app: NewYorkCancer) =>
  ValidNewYorkCancer.createApp({...app, section125});

export const changeRiderInformation = (riderAmount: number, riderCode: string, index: number) => (
  app: NewYorkCancer
) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    app.riderInformation.amounts,
    app.riderInformation.codes
  );

  return ValidNewYorkCancer.createApp({
    ...app,
    riderInformation: new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes),
  });
};

export const policyOptions = ['A', 'B', 'C'];

export const getAllRiderCodes = () => riderCodes;
